<template>
	<div class="grid">
		<SpeV2Filters :currentUser="currentUser" :company="company" :vendorId="vendorId" @search="onFilterSearch"
			@clear="onFilterClear" />
		<div class="col-12 p-fluid mb-4 spe-v2-results">
			<div class="content-wrapper">
				<div class="content-header mb-4">Search Results</div>
				<SpeV2ResultTable :value="dataList" :loading="isLoading" :total-records="totalRecords"
					:show-supplier-name="true" :status-label="statusLabel" :status-class="statusClass"
					@page="handlePagination" @view-detail="onViewDetail" @review-pdf="onReviewPdf" />
			</div>
		</div>
	</div>
</template>

<script setup>
import { ref, watch, onMounted, defineProps, defineEmits, defineExpose } from 'vue';
import SpeV2Service from '../../service/SpeV2Service';
import SpeV2Filters from './SpeV2Filters.vue';
import SpeV2ResultTable from './SpeV2ResultTable.vue';

const props = defineProps({
	currentUser: { type: Object, default: null },
	company: { type: Object, default: null },
	vendorId: { type: [String, Number], default: null },
	onReviewPdf: { type: Function, default: null },
});

const emit = defineEmits(['view-detail', 'review-pdf']);

const dataList = ref([]);
const totalRecords = ref(0);
const isLoading = ref(false);
const lazyParams = ref({ first: 0, page: 0, rows: 10 });
const activeFilters = ref({});

const statusLabel = (status) => {
	const map = { O: 'Outstanding', B: 'Pending CAPA Proxy', P: 'Completed', R: 'Rejected', D: 'Dismissed' };
	return map[status] || status;
};

const statusClass = (status) => {
	const map = { B: 'chip-submitted' };
	return map[status] || '';
};

const canLoadData = () => {
	if (!props.company || !props.currentUser) return false;
	const leveltenant = props.currentUser.leveltenant;
	return leveltenant == 1 || leveltenant == 3;
};

const buildFilterParams = () => {
	const params = {};
	const { year, semester, category, method, score_grade, vendor_id } = activeFilters.value;

	if (year != null && year !== '') params.year = year;
	if (semester != null && semester !== '') params.semester = semester;
	if (category) params.category = category;
	if (method) params.method = method;
	if (score_grade) params.score_grade = score_grade;
	if (vendor_id != null && vendor_id !== '') params.vendor_id = String(vendor_id);

	return params;
};

const loadData = async () => {
	if (!canLoadData()) return;

	isLoading.value = true;
	try {
		const params = {
			org_id: props.company.value,
			db_instance: props.company.db_instance,
			leveltenant: props.currentUser.leveltenant,
			page: lazyParams.value.page + 1,
			limit: lazyParams.value.rows,
			...buildFilterParams(),
		};

		const res = await SpeV2Service.getPendingCapaProxy(params);
		if (res.status === 200) {
			dataList.value = Array.isArray(res.data.data) ? res.data.data : [];
			totalRecords.value = Number(res.data.count) || 0;
		}
	} catch (err) {
		console.log(err);
	}
	isLoading.value = false;
};

const handlePagination = (event) => {
	lazyParams.value = event;
	loadData();
};

const onFilterSearch = (filters) => {
	activeFilters.value = filters;
	lazyParams.value = { ...lazyParams.value, first: 0, page: 0 };
	loadData();
};

const onFilterClear = () => {
	activeFilters.value = {};
	lazyParams.value = { ...lazyParams.value, first: 0, page: 0 };
	loadData();
};

const onViewDetail = (scoringId) => {
	emit('view-detail', scoringId, 'pending-capa-proxy');
};

const onReviewPdf = (scoringId, category) => {
	if (props.onReviewPdf) {
		props.onReviewPdf(scoringId, category);
		return;
	}
	emit('review-pdf', scoringId, category);
};

watch(
	() => [props.company, props.vendorId],
	() => {
		activeFilters.value = {};
		lazyParams.value = { first: 0, page: 0, rows: lazyParams.value.rows || 10 };
		if (canLoadData()) {
			loadData();
		}
	}
);

onMounted(() => {
	if (canLoadData()) {
		loadData();
	}
});

defineExpose({ reload: loadData, getActiveFilters: buildFilterParams });
</script>

<style scoped>
.chip-submitted {
	background: #0ea5e9;
	color: #fff;
}
</style>
